import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import server from '../../../api/server';
import Card from '../ui/Card';
import Button from '../ui/Button';
import AvatarCircle from '../ui/AvatarCircle';
import AddClientModal from './AddClientModal';
import EditClientModal from './EditClientModal';
import DeleteClientModal from './DeleteClientModal';
import { PlusIcon, PencilIcon, TrashIcon, CopyIcon, ExternalLinkIcon } from '../ui/icons';

// Redesign of newadmin/ApplicantList.js — same /admin/checkApplicants and
// /admin/moveClient calls, same add/edit/delete client thunks via the modals.
const ApplicantListCard = () => {
    const applicants = useSelector((s) => s.admin.active_application.application.applicants);
    const formId = useSelector((s) => s.admin.active_application.form.id);

    const [copied, setCopied] = useState(false);
    const [failingApplicants, setFailingApplicants] = useState([]);
    const [addOpen, setAddOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [editClient, setEditClient] = useState({ name: '', email: '', mobile: '' });
    const [editClientId, setEditClientId] = useState('');
    const [deleteClient, setDeleteClient] = useState({ name: '' });

    const checkApplicants = async () => {
        const response = await server.post('/admin/checkApplicants', {
            application_id: `application-${formId}`,
            applicants: applicants,
        });
        if (response && response.data && response.data.pass == false) {
            setFailingApplicants(response.data.fails);
        } else if (response.data.pass == true) {
            setFailingApplicants([]);
        }
    };

    useEffect(() => {
        checkApplicants();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const moveClient = async () => {
        const response = await server.post('/admin/moveClient', {
            application_id: `application-${formId}`,
            firebase_id: editClient.firebase_id,
        });
        if (response && response.data.success) {
            checkApplicants();
        }
    };

    const openEdit = (client, key) => {
        setEditClient(client);
        setEditClientId(key);
        setEditOpen(true);
    };

    const openDelete = (client) => {
        setDeleteClient(client);
        setDeleteOpen(true);
    };

    const renderErrorMessage = () => {
        if (failingApplicants.length > 0) {
            let message = 'The following applicants are currently attached to another application: ';
            failingApplicants.forEach((id) => {
                message += `${applicants[id].name}, `;
            });
            return (
                <div className="mx-5 sm:mx-6 mb-5 rounded-xl px-4 py-3 text-sm font-semibold" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
                    {message}
                </div>
            );
        }
        return null;
    };

    const keys = Object.keys(applicants || {});

    return (
        <>
            <AddClientModal open={addOpen} onClose={() => setAddOpen(false)} />
            <EditClientModal
                open={editOpen}
                onClose={() => setEditOpen(false)}
                client={editClient}
                clientId={editClientId}
                onMoveClient={moveClient}
            />
            <DeleteClientModal open={deleteOpen} onClose={() => setDeleteOpen(false)} client={deleteClient} />

            <Card
                title="Clients"
                subtitle="All borrowers linked to this application."
                actions={
                    <>
                        <Button variant="secondary" onClick={() => setAddOpen(true)}>
                            <PlusIcon className="h-4 w-4" />
                            Add Client
                        </Button>
                        <a
                            href={`/dashboard/application-${formId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-2 rounded-xl border-2 bg-white px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-[#F5F6FA]"
                            style={{ borderColor: '#D1D5DB', color: '#1F2937' }}
                        >
                            <ExternalLinkIcon className="h-4 w-4" />
                            Dashboard
                        </a>
                        <CopyToClipboard text={`${process.env.REACT_APP_DOMAIN}client`}>
                            <Button onClick={() => setCopied(true)} title="Copy Client Link">
                                <CopyIcon className="h-4 w-4" />
                                {copied ? 'Copied!' : 'Copy Client Link'}
                            </Button>
                        </CopyToClipboard>
                    </>
                }
                bodyClassName="p-0"
            >
                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-[#F0F1F5]">
                                <th className="px-4 sm:pl-6 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">Name</th>
                                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">Email</th>
                                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">Mobile</th>
                                <th className="px-4 sm:pr-6 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#F0F1F5]">
                            {keys.map((key) => {
                                const applicant = applicants[key];
                                return (
                                    <tr key={key}>
                                        <td className="px-4 sm:pl-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <AvatarCircle name={applicant.name} size="md" />
                                                <span className="text-sm font-bold text-[#1F2937]">{applicant.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 text-sm text-[#6B7280]">{applicant.email}</td>
                                        <td className="px-4 py-4 text-sm text-[#6B7280]">{applicant.mobile}</td>
                                        <td className="px-4 sm:pr-6 py-4 text-right whitespace-nowrap">
                                            <button
                                                type="button"
                                                onClick={() => openEdit(applicant, key)}
                                                title="Edit client"
                                                className="rounded-lg p-2 text-[#6B7280] hover:bg-[#EFEAFE] hover:text-[#5B3FE0] transition-colors"
                                            >
                                                <PencilIcon className="h-4 w-4" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => openDelete(applicant)}
                                                title="Delete client"
                                                className="rounded-lg p-2 text-[#6B7280] hover:bg-[#FEE2E2] hover:text-[#DC2626] transition-colors ml-1"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Mobile card list */}
                <ul className="md:hidden divide-y divide-[#F0F1F5]">
                    {keys.map((key) => {
                        const applicant = applicants[key];
                        return (
                            <li key={key} className="flex items-center gap-3 px-4 py-4">
                                <AvatarCircle name={applicant.name} size="md" />
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-bold text-[#1F2937] truncate">{applicant.name}</p>
                                    <p className="text-xs text-[#6B7280] truncate">{applicant.email}</p>
                                    <p className="text-xs text-[#6B7280]">{applicant.mobile}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => openEdit(applicant, key)}
                                    title="Edit client"
                                    className="rounded-lg p-2 text-[#6B7280] hover:bg-[#EFEAFE] hover:text-[#5B3FE0] transition-colors"
                                >
                                    <PencilIcon className="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => openDelete(applicant)}
                                    title="Delete client"
                                    className="rounded-lg p-2 text-[#6B7280] hover:bg-[#FEE2E2] hover:text-[#DC2626] transition-colors"
                                >
                                    <TrashIcon className="h-4 w-4" />
                                </button>
                            </li>
                        );
                    })}
                </ul>

                {renderErrorMessage()}
            </Card>
        </>
    );
};

export default ApplicantListCard;
